import type { Ref } from 'vue'

import { useCan } from '@twinepos/vue-rbac'
import { computed, ref, watch } from 'vue'

import type { CommandCenterOptions } from '@/domains/command-center/composables/use-command-center/useCommandCenter.types'
import type { ConfirmationHandlers } from '@/domains/command-center/handlers/product/productCommands.types'
import type { ActionCommand, MenuItem, SearchResultCommand, WorkflowState } from '@/domains/command-center/types'

import {
  injectedSearchResults,
  isBarcodeLookupPending,
  isInjectionInFlight,
  registerBarcodeScopeReader,
  scanBlockedBarcode,
  setScanBlockedBarcode
} from '@/composables/use-barcode-scanner/useBarcodeInjector'
import { useConfirmationService } from '@/composables/use-confirmation-service/useConfirmationService'
import { useMenuStore } from '@/domains/admin-panel/store/use-menu-store/useMenuStore'
import { executeCommand } from '@/domains/command-center/commandExecutor'
import { generateCommands, generateMenuItems } from '@/domains/command-center/commandGenerator'
import { getAllCategories, isUsable, lockCategory } from '@/domains/command-center/commandRegistry'
import { useSearchScopes } from '@/domains/command-center/composables/use-search-scopes/useSearchScopes'
import { searchItems } from '@/domains/command-center/services/searchService'
import { parseTypedBarcodes } from '@/domains/command-center/utils/barcodeUtils'
import { getPlaceholder } from '@/domains/command-center/workflow/workflowManager'
import { useToastStore } from '@/domains/notifications/store/use-toast-store/useToastStore'
import { debounceWithFixedDelay } from '@/utils/delay-util/delayUtil'
import { generateComplexId } from '@/utils/math-util/mathUtil'

const SEARCH_COMPLETE_TIMEOUT_MS = 5000

export const useCommandCenterActions = (query: Ref<string | undefined>, options: CommandCenterOptions = {}) => {
  const isSuspended = options.isSuspended ?? ref(false)
  const loading = ref(false)
  const searchResults = ref<SearchResultCommand[]>([])
  const workflowState = ref<WorkflowState>({
    mode: 'idle',
    pendingBarcodesForVariants: []
  })

  // Initialize services in component context (required by PrimeVue)
  const { confirmDuplicateSku } = useConfirmationService()
  const toastStore = useToastStore()

  // Sidebar pages become searchable rows. The server withholds every section the
  // user may not see, so none of them can reach the palette.
  const menuStore = useMenuStore()

  // Which scopes the user may search, and which of them are switched on. `activeScopes` is the
  // only list `searchItems` ever sees, so an unpermitted endpoint is never called.
  const { availableScopes, activeScopes, isScopeActive, toggleScope, activateScope, resetScopes } = useSearchScopes()

  const isProductScopeAvailable = computed(() => availableScopes.value.some(scope => scope.id === 'product'))

  // The scanner pipeline obeys the pills, and the pills live here. It reads through the injector
  // rather than importing this composable, because both mount as siblings in AppLayout.
  registerBarcodeScopeReader(() => ({
    isAvailable: isProductScopeAvailable.value,
    // Stock mode reads its scans through the same Products scope, so the pill's state does not
    // apply there: it asks for the scope whatever the pill says.
    scope: isSuspended.value
      ? availableScopes.value.find(scope => scope.id === 'product')
      : activeScopes.value.find(scope => scope.id === 'product'),
    canNavigate: !isSuspended.value
  }))

  /** Switching Products on is exactly what the blocked-scan row asks for, so it clears itself. */
  const searchScannedBarcode = () => {
    activateScope('product')
    setScanBlockedBarcode(undefined)
  }

  const { can } = useCan()

  /**
   * Every curated category, with the ones the user may not use locked rather than removed.
   *
   * A category declares the permission, or permissions, its actions navigate behind. Locking keeps
   * the palette's shape the same for everyone and says why a row is unavailable, instead of offering
   * a command that 403s on arrival. Pages carry no permission — the server already withheld those
   * sections.
   */
  const categories = computed(() =>
    getAllCategories([...menuStore.mainMenuMenuSections, ...menuStore.settingsMenuSections]).map(category =>
      isUsable(category, can) ? category : lockCategory(category)
    )
  )

  // Track active toasts for proper cleanup
  const activeToasts = new Map<string, { id: string }>()

  // Create toast handlers for SKU checking flow
  const toastHandlers = {
    showSkuCheckingToast: (barcode: string): string => {
      const id = generateComplexId()
      toastStore.add({
        id,
        state: 'init',
        severity: 'info',
        summary: 'Checking SKU Availability',
        detail: `Verifying if ${barcode} is already registered...`
      })
      activeToasts.set(id, { id })
      return id
    },
    removeToast: (id: string): void => {
      // Use the toast store's maintenance system - adding a completing state removes the init toast
      if (activeToasts.has(id)) {
        toastStore.add({
          id,
          state: 'success',
          severity: 'secondary',
          summary: '',
          detail: '',
          life: 1 // Minimal life to dismiss without showing content
        })
        activeToasts.delete(id)
      }
    },
    showInfoToast: (summary: string, detail: string): void => {
      toastStore.add({
        state: 'once',
        severity: 'info',
        summary,
        detail
      })
    },
    showWarningToast: (summary: string, detail: string): void => {
      toastStore.add({
        state: 'warn',
        severity: 'warn',
        summary,
        detail
      })
    }
  }

  const confirmationHandlers: ConfirmationHandlers = {
    confirmDuplicateSku,
    toast: toastHandlers
  }

  /**
   * The codes a TYPED query may seed a product with.
   *
   * Strict shapes only — the loose scanner rule accepted `TS1042`, so typing an order number
   * offered to generate a product from it. A scanned code reaches the same actions through the
   * injector, which has already proved its intent by arrival speed.
   */
  const currentParsedBarcodes = computed(() => parseTypedBarcodes(query.value?.trim()))

  const dynamicPlaceholder = computed(() => getPlaceholder(workflowState.value))

  // Reset workflow state helper
  const resetWorkflow = () => {
    workflowState.value = {
      mode: 'idle',
      pendingBarcodesForVariants: []
    }
    query.value = ''
  }

  const workflowStateMode = computed(() => workflowState.value.mode)

  // Clear query input helper
  const clearQuery = () => {
    query.value = ''
  }

  /**
   * Wait for any in-progress search to complete
   * Returns a promise that resolves with the latest search results
   * Has a timeout to prevent indefinite waiting
   */
  const waitForSearchComplete = (): Promise<SearchResultCommand[]> => {
    return new Promise(resolve => {
      // If not loading, resolve immediately with current results
      if (!loading.value) {
        resolve(searchResults.value)
        return
      }

      // Set up timeout
      const timeoutId = setTimeout(() => {
        stopWatcher()
        resolve(searchResults.value)
      }, SEARCH_COMPLETE_TIMEOUT_MS)

      // Watch for loading to become false
      const stopWatcher = watch(
        loading,
        newLoading => {
          if (!newLoading) {
            clearTimeout(timeoutId)
            stopWatcher()
            resolve(searchResults.value)
          }
        },
        { immediate: false }
      )
    })
  }

  const fetchSearchResults = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      searchResults.value = []
      return
    }

    loading.value = true
    try {
      searchResults.value = await searchItems(searchTerm, activeScopes.value)
    } catch {
      searchResults.value = []
    } finally {
      loading.value = false
    }
  }

  /**
   * A debounced search that refuses to answer a question nobody is asking any more.
   *
   * ⚠ **This replaced a one-shot suppression flag, and the flag was the bug.** `injectBarcode`
   * raised it so a debounce scheduled before the injection could not overwrite the injected
   * results. But the query watcher skips the debounce outright while an injection is in flight, so
   * nothing consumed the flag — it survived, and the next fetch from ANY source swallowed itself.
   * That source was the scope pills: pasting a barcode with Products off, then switching Products
   * on, made no request at all. Switching off and on again worked, because the flag was spent by
   * then.
   *
   * Comparing the term against the live query solves the same race without leaving state behind: a
   * debounce whose term is stale is exactly the one that must not run, whatever made it stale.
   */
  const debouncedFetch = debounceWithFixedDelay((searchTerm: string) => {
    if (searchTerm !== (query.value?.trim() ?? '')) return

    void fetchSearchResults(searchTerm)
  })

  // Watch for query changes to trigger search
  // Skip debounced fetch when barcode was injected (results come via injectedSearchResults watcher)
  watch(
    query,
    newQueryValue => {
      if (isInjectionInFlight.value || isSuspended.value) return
      void debouncedFetch(newQueryValue?.trim() ?? '')
    },
    { immediate: false }
  )

  // Turning a pill on or off changes the question, not the query, so the search re-runs against
  // the new scope set. Without this the palette keeps answering the previous set until the user
  // types another character.
  watch(activeScopes, () => {
    if (isSuspended.value) return

    const searchTerm = query.value?.trim() ?? ''
    if (searchTerm.length < 2) return

    void fetchSearchResults(searchTerm)
  })

  // When barcode scanner injects pre-fetched results, populate directly (no API call)
  watch(injectedSearchResults, results => {
    // A scan in stock mode is stock mode's. Taken here, its results would show once stock mode closed.
    if (results !== undefined && !isSuspended.value) {
      searchResults.value = results
    }
  })

  // Stock mode hands the box back with whatever it held. Ask for that, rather than show the last answer.
  watch(isSuspended, isNowSuspended => {
    if (isNowSuspended) return

    void fetchSearchResults(query.value?.trim() ?? '')
  })

  // A fresh barcode lookup started — drop the previous barcode's results so the
  // menu shows only "Searching…", not stale matches, until the new results land.
  watch(isBarcodeLookupPending, pending => {
    if (pending) searchResults.value = []
  })

  // Generate effective commands and menu items based on current state
  const allEffectiveCommands = computed<(ActionCommand | SearchResultCommand)[]>(() => {
    return generateCommands(
      query.value,
      currentParsedBarcodes.value,
      searchResults.value,
      workflowState.value,
      resetWorkflow,
      clearQuery,
      loading.value,
      waitForSearchComplete,
      confirmationHandlers,
      categories.value
    )
  })

  // A scanned barcode is looked up by the scanner pipeline, which suppresses the
  // Command Center's own debounced fetch — so `loading` stays false during that
  // lookup. Fold the scanner's in-flight flag in so the menu reads "Searching…"
  // rather than "No exact match" until results arrive.
  const isSearching = computed(() => loading.value || isBarcodeLookupPending.value)

  const menuItems = computed<MenuItem[]>(() => {
    return generateMenuItems(
      query.value,
      currentParsedBarcodes.value,
      searchResults.value,
      allEffectiveCommands.value,
      workflowState.value,
      isSearching.value,
      categories.value,
      scanBlockedBarcode.value,
      isScopeActive('product')
    )
  })

  // Execute command handler
  const handleExecuteCommand = async (item: MenuItem) => {
    return executeCommand(
      item,
      allEffectiveCommands.value,
      workflowState.value,
      newState => (workflowState.value = newState),
      clearQuery
    )
  }

  return {
    loading,
    menuItems,
    executeCommand: handleExecuteCommand,
    dynamicPlaceholder,
    workflowStateMode,
    availableScopes,
    isScopeActive,
    toggleScope,
    activateScope,
    resetScopes,
    isProductScopeAvailable,
    searchScannedBarcode
  }
}
